/**
 * FinalizeModal — external-attester report sign-off.
 *
 * Closes the CP 04 11 grounding hardening (Phase 1b) gap: the backend
 * finalize endpoint 412-gates on an unacknowledged acknowledgment-required
 * verifier WARNING (`endorsement_grounding` — a possible fabricated/ungrounded
 * coverage assertion), but until now NO frontend surfaced that warning or
 * collected the acknowledgment, leaving the gate inert. This modal:
 *
 *   1. Fetches the REPORT-LEVEL verifier report on open.
 *   2. If an acknowledgment-required WARNING is present, renders it and
 *      REQUIRES an explicit checkbox before the attester can finalize.
 *   3. Sends `acknowledged_verifier_run_id = verifier.run_id` so the backend
 *      gate is satisfied through a deliberate human action — never blindly.
 *
 * Mirrors the per-engine {@link ApproveModal} warning-acknowledgment pattern,
 * raised to the report level where `endorsement_grounding` actually lives
 * (it is a deliberation-level check, so it never appears in a per-engine modal).
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import Modal from '../../ui/Modal';
import { Button } from '../../ui/Button';
import CpaWorkpaperTextarea from '../../privilege/CpaWorkpaperTextarea';
import { getReportVerifier, finalizeAttestation } from '../../../services/engineReviewApi';
import getCaseProfile from '../../../services/caseProfileService';
import type { CaseProfilePayload } from '../../shared/caseProfile';
import {
  ACK_REQUIRED_VERIFIER_CHECKS,
  type ReportVerifierReport,
  type ReportVerifierCheck,
  type FinalizeResponse,
} from '../../../types/engineReview';

// =============================================================================
// COMPONENT
// =============================================================================

export interface FinalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  /** Called with the finalize response after a successful sign-off. */
  onFinalized?: (res: FinalizeResponse) => void;
}

const ACK_REQUIRED: readonly string[] = ACK_REQUIRED_VERIFIER_CHECKS;

/** Acknowledgment-required WARNING checks in a report-level verifier report. */
function ackRequiredWarnings(
  verifier: ReportVerifierReport | null,
): ReportVerifierCheck[] {
  if (!verifier) return [];
  return (verifier.checks_failed ?? []).filter(
    (c) => ACK_REQUIRED.includes(c.check_name) && c.severity === 'warning',
  );
}

export default function FinalizeModal({
  isOpen,
  onClose,
  reportId,
  onFinalized,
}: FinalizeModalProps) {
  const [verifier, setVerifier] = useState<ReportVerifierReport | null>(null);
  const [loadingVerifier, setLoadingVerifier] = useState(false);
  const [caseProfile, setCaseProfile] = useState<CaseProfilePayload | null>(null);
  const [signature, setSignature] = useState('');
  const [notes, setNotes] = useState('');
  const [acknowledges, setAcknowledges] = useState(false);
  const [acknowledgesPosture, setAcknowledgesPosture] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the report-level verifier whenever the modal opens. A 404 (no
  // deliberation has produced a report yet) is benign — there is simply no
  // warning to acknowledge, so finalize proceeds on signature alone.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingVerifier(true);
    setError(null);
    getReportVerifier(reportId)
      .then((report) => {
        if (!cancelled) setVerifier(report);
      })
      .catch(() => {
        if (!cancelled) setVerifier(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingVerifier(false);
      });
    getCaseProfile(reportId)
      .then((profile) => {
        if (!cancelled) setCaseProfile(profile);
      })
      .catch(() => {
        if (!cancelled) setCaseProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, reportId]);

  const warnings = ackRequiredWarnings(verifier);
  const hasAckWarning = warnings.length > 0;
  // Only an explicit, loaded `posture_confirmed === false` warns — a null
  // profile (legacy/404) is treated as NOT unconfirmed.
  const postureUnconfirmed = !!caseProfile && caseProfile.posture_confirmed === false;
  const canSubmit =
    !loadingVerifier &&
    !submitting &&
    signature.trim().length > 0 &&
    (!hasAckWarning || acknowledges) &&
    (!postureUnconfirmed || acknowledgesPosture);

  const resetAndClose = () => {
    setSignature('');
    setNotes('');
    setAcknowledges(false);
    setAcknowledgesPosture(false);
    setError(null);
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await finalizeAttestation(reportId, {
        signature_confirmation: signature.trim(),
        final_notes: notes.trim() || undefined,
        // Only echo the run_id when a real acknowledgment-required warning is
        // present AND the attester checked the box — never blind-acknowledge.
        acknowledged_verifier_run_id:
          hasAckWarning && acknowledges && verifier ? verifier.run_id : undefined,
      });
      setSubmitting(false);
      onFinalized?.(res);
      resetAndClose();
    } catch (err) {
      setSubmitting(false);
      setError(err instanceof Error ? err.message : 'Finalize failed.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title="Finalize attestation"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={resetAndClose}
            disabled={submitting}
            data-testid="finalize-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="finalize-modal-submit"
          >
            {submitting ? 'Finalizing…' : 'Sign &amp; finalize'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4" data-testid="finalize-modal">
        {loadingVerifier && (
          <div
            className="flex items-center gap-2 text-sm"
            style={{ color: 'var(--color-text-muted)' }}
            data-testid="finalize-modal-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking verifier status…
          </div>
        )}

        {!loadingVerifier && !hasAckWarning && (
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            You are about to finalize and attest to this report. This signs the
            forensic package for downstream (attorney) use and is recorded in
            the hash-chained attestation history.
          </p>
        )}

        {/* Acknowledgment-required WARNING (e.g. a possible fabricated CP 04 11
            coverage assertion). The attester MUST see and acknowledge it. */}
        {!loadingVerifier && hasAckWarning && (
          <>
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
              data-testid="finalize-endorsement-warning"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div>
                  <h4 className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                    Verifier warning — review before signing
                  </h4>
                  <p className="mt-1 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                    The post-deliberation verifier flagged one or more items
                    that require your explicit review before you attest — a
                    possible fabricated or ungrounded coverage assertion, or a
                    material divergence between the two BI methods behind the
                    reconciled figure. Review each flagged item below; your
                    signature makes this package forward-facing to the attorney.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {warnings.map((w) => (
                      <li
                        key={w.check_name}
                        className="text-sm flex items-start gap-2"
                        style={{ color: 'var(--color-needs-human-text)' }}
                      >
                        <span className="mt-0.5" style={{ color: 'var(--color-brand)' }}>-</span>
                        <span>
                          <strong>{w.check_name}:</strong>{' '}
                          {w.details || 'see verifier report'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledges}
                onChange={(e) => setAcknowledges(e.target.checked)}
                className="mt-1 h-4 w-4 rounded focus:ring-[color:var(--color-brand)]"
                style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
                data-testid="finalize-modal-acknowledge"
              />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                I have reviewed the flagged item(s) above and confirm this
                report is acceptable to finalize.
              </span>
            </label>
          </>
        )}

        {/* Case posture unconfirmed (CT-0 decision #4). Warning-mode surfacing
            of the finalize endpoint's 412 posture gate — a deliberate human
            acknowledgment, never a posture *confirmation*. */}
        {postureUnconfirmed && (
          <>
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
              data-testid="finalize-posture-warning"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div>
                  <h4 className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                    Case posture unconfirmed — review before signing
                  </h4>
                  <p className="mt-1 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                    This case&apos;s posture (first-party vs third-party) has
                    not been confirmed by an attorney or the intake wizard, so
                    the settlement is capped at the first-party supported net
                    (CT-0 decision #4). Confirm posture before finalizing if a
                    third-party path applies — your signature seals this
                    figure.
                  </p>
                  <p className="mt-2 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                    Working posture: {caseProfile?.posture ?? '—'} (inferred, unconfirmed)
                  </p>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledgesPosture}
                onChange={(e) => setAcknowledgesPosture(e.target.checked)}
                className="mt-1 h-4 w-4 rounded focus:ring-[color:var(--color-brand)]"
                style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
                data-testid="finalize-modal-acknowledge-posture"
              />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                I acknowledge the case posture is unconfirmed and accept
                finalizing on the capped first-party basis.
              </span>
            </label>
          </>
        )}

        {/* Signature — must match the attester name on file (backend 400s
            otherwise). */}
        <div>
          <label
            htmlFor="finalize-signature"
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Type your full name to sign
          </label>
          <input
            id="finalize-signature"
            type="text"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Full name exactly as on file"
            className="w-full rounded-md px-3 py-2 text-sm focus:border-[color:var(--color-brand)] focus:ring-[color:var(--color-brand)]"
            style={{ border: '1px solid var(--color-border)' }}
            data-testid="finalize-modal-signature"
          />
        </div>

        <CpaWorkpaperTextarea
          label="Final notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Any notes to record with the attestation…"
        />

        {error && (
          <div
            className="rounded-md px-3 py-2 text-sm"
            style={{ border: '1px solid var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
            data-testid="finalize-modal-error"
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
